import {
  ConsentCategory,
  type TConsentCategory,
} from 'public/assets/js/analytics/utilities';
import { type FC, useCallback, useEffect, useRef, useState } from 'react';
import { useTrack } from 'src/contexts';
import { NEXT_PUBLIC_IS_PREVIEW, NEXT_PUBLIC_PERCY_RUN } from 'src/env';
import { useIsSegmentLoaded } from 'src/hooks/useIsSegmentLoaded';
import { OneTrustPageMarker } from './OneTrustHeadTags';
import ThirdPartyScripts from './ThirdPartyScripts';
import {
  getActiveConsentCategories,
  identifyUser,
  initializeAnalytics,
} from './utils';

/** Stateful value that tracks if OneTrust has loaded with consent preferences. */
export const useIsOneTrustLoaded = () => {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    if (window.OnetrustActiveGroups) {
      setIsLoaded(true);
      return undefined;
    }

    const listener = () => {
      setIsLoaded(true);
    };

    window.addEventListener('OneTrustGroupsUpdated', listener, { once: true });
    return () => {
      window.removeEventListener('OneTrustGroupsUpdated', listener);
    };
  }, []);

  return isLoaded;
};

/**
 * OneTrust categories at page load. This intentionally does not
 * listen for changes in OneTrust consent since we reload
 * the page to reflect any consent changes.
 *
 * Takes `isOneTrustLoaded` as an argument (instead of calling
 * `useIsOneTrustLoaded` internally) so the parent owns the single
 * `OneTrustGroupsUpdated` event listener.
 */
const useLoadedOneTrustCategories = (isOneTrustLoaded: boolean) => {
  const [enabledCategories, setEnabledCategories] = useState<
    TConsentCategory[]
  >([]);

  useEffect(() => {
    if (!isOneTrustLoaded) {
      return;
    }

    setEnabledCategories(getActiveConsentCategories());
  }, [isOneTrustLoaded]);

  return enabledCategories;
};

/**
 * Listens for changes to OneTrust consent changes and handles accordingly.
 *
 * Accepts the loaded/ready flags and current categories as arguments
 * so the parent component owns the single instance of each primitive
 * hook (avoids redundant listeners and polling intervals).
 */
const useWatchOneTrustCategories = ({
  enabledCategories,
  isOneTrustLoaded,
  isSegmentLoaded,
}: {
  enabledCategories: TConsentCategory[];
  isOneTrustLoaded: boolean;
  isSegmentLoaded: boolean;
}) => {
  const track = useTrack();

  /** Keeps track if overall listener hook is active */
  const isListenerActive = useRef(false);

  /**
   * `window.OneTrust.OnConsentChanged` callback reference, since registering functions this
   * way does not support multiple callbacks with the same function body structure.
   */
  const handleRef = useRef<
    ((newCategories: TConsentCategory[]) => Promise<void>) | null
  >(null);

  const handle = useCallback(
    async (newCategories: TConsentCategory[]) => {
      // Instrument when the user consents to everything
      if (newCategories.length === Object.values(ConsentCategory).length) {
        track({ type: 'CONSENT_BANNER_ACCEPT_ALL' });
      }

      if (isSegmentLoaded) {
        await identifyUser(newCategories);
      }

      // Reload page when user changes consent categories to ensure
      // all marketing integrations are re-initialized.
      if (
        // If lists don't match length-wise
        enabledCategories.length !== newCategories.length ||
        // Or lengths match but values are different
        !enabledCategories.every((v) => {
          return newCategories.includes(v);
        })
      ) {
        // Reload page to apply changes
        window.location.reload();
      }
    },
    [isSegmentLoaded, enabledCategories, track],
  );

  // Synchronize callback reference with current handler
  useEffect(() => {
    handleRef.current = handle;
  }, [handle]);

  useEffect(() => {
    if (!isOneTrustLoaded) {
      return;
    }

    isListenerActive.current = true;

    // We opted for `OnConsentChanged` instead of `OTConsentApplied` because the latter
    // would fire on page unload. This complicates implementation since it's not a
    // window event listener, so we have to manage lifecycle ourselves.
    //
    // Calling `window.OneTrust.OnConsentChanged` multiple times does not
    // result in multiple event listeners since OneTrust internally
    // dedupes by calling `func.toString()` 🫠.
    window.OneTrust.OnConsentChanged(async () => {
      if (!isListenerActive.current || !handleRef.current) {
        return;
      }

      handleRef.current(getActiveConsentCategories());
    });

    return () => {
      isListenerActive.current = false;
    };
  }, [isOneTrustLoaded, handle]);
};

/**
 * In the U.S., Twilio Segment is always loaded and is placed as a subcategory of Strictly Necessary
 * in OneTrust. In the EU, it requires an explicit opt-in of either Functional, Performance, or
 * Targeting cookies;iIn OneTrust this is placed under the Functional category due to UI
 * limitations. We must enable Segment for Functional + Targeting as these switches
 * will largely not actually do anything without Segment enabled.
 *
 * @see https://app.onetrust.com/cookies/templates
 */
const isTwilioSegmentConsentedTo = (enabledCategories: TConsentCategory[]) => {
  return (
    enabledCategories.includes(ConsentCategory.PERFORMANCE) ||
    enabledCategories.includes(ConsentCategory.FUNCTIONAL) ||
    enabledCategories.includes(ConsentCategory.TARGETING) ||
    enabledCategories.includes(ConsentCategory.TWILIO_SEGMENT)
  );
};

const ConsentManager: FC = () => {
  const isOneTrustLoaded = useIsOneTrustLoaded();
  const isSegmentLoaded = useIsSegmentLoaded();
  const enabledCategories = useLoadedOneTrustCategories(isOneTrustLoaded);
  const isSegmentInitialized = useRef(false);
  const track = useTrack();

  useWatchOneTrustCategories({
    enabledCategories,
    isOneTrustLoaded,
    isSegmentLoaded,
  });

  useEffect(() => {
    if (
      !isSegmentLoaded ||
      !isOneTrustLoaded ||
      !isTwilioSegmentConsentedTo(enabledCategories)
    ) {
      return;
    }

    if (!isSegmentInitialized.current) {
      isSegmentInitialized.current = true;
      initializeAnalytics(enabledCategories);
    }
  }, [isSegmentLoaded, isOneTrustLoaded, enabledCategories]);

  // Instrument OneTrust consent banner
  useEffect(() => {
    if (!isOneTrustLoaded || !isSegmentLoaded) {
      return;
    }

    if (!window.OneTrust.IsAlertBoxClosed()) {
      track({
        type: 'CONSENT_BANNER_VIEW',
        payload: {
          nonInteraction: 1,
        },
      });
    }
  }, [isOneTrustLoaded, isSegmentLoaded, track]);

  if (NEXT_PUBLIC_PERCY_RUN || NEXT_PUBLIC_IS_PREVIEW) {
    return null;
  }

  return (
    <>
      <OneTrustPageMarker />
      <ThirdPartyScripts enabledCategories={enabledCategories} />
    </>
  );
};

export default ConsentManager;

/* eslint-disable @typescript-eslint/naming-convention */
declare global {
  interface Window {
    /**
     * Partially typed
     * @see https://developer.onetrust.com/onetrust/docs/javascript-api
     */
    OneTrust: {
      IsAlertBoxClosed: () => boolean;
      OnConsentChanged: (callback: () => unknown) => void;
      ToggleInfoDisplay: () => void;
      getGeolocationData?: () => { country?: string };
    };
    OnetrustActiveGroups: string; // e.g. `",C0001,SGMNT,C0003,C0002,C0004,"`
    /** Set by the OneTrust loader in OneTrustHeadTags so it runs once per page. */
    __plaidOneTrustLoader?: boolean;
  }
}
/* eslint-enable @typescript-eslint/naming-convention */
